'use client';

import type { ColumnDef, SortingState, Table as ReactTable } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnFiltersState, PaginationState } from '@tanstack/table-core';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'shared/lib/i18n';
import mergeClasses from 'shared/lib/utils';
import { Collapsible, CollapsibleContent } from 'shared/ui/collapsible';
import { DataTablePagination } from 'shared/ui/data-table-pagination';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'shared/ui/table';
import * as m from '@paraglide/messages';
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[] | undefined;
  rowsPerPage: number[];
  rowCount?: number;
  pageCount?: number;
  initialFilters?: ColumnFiltersState;
  variant?: 'default' | 'list';
  withPagination?: boolean;
  isLink?: boolean;
  getRowLink?: (row: TData) => string;
  collapsible?: boolean;
  collapseComponent?: React.ComponentType<{
    row: TData;
  }>;
  dataTableTools?: React.ComponentType<{
    table: ReactTable<TData>;
    onFilterChange: ((filters: any) => void) | undefined;
  }>;
  onFilterChange?: (filters: any) => void;
  manualPagination?: boolean;
  pagination?: PaginationState;
  setPagination?: Dispatch<SetStateAction<PaginationState>>;
  getRowClassName?: (row: TData) => string;
}
export function DataTable<TData, TValue>({
  columns,
  data,
  rowsPerPage,
  rowCount,
  pageCount,
  initialFilters,
  collapsible = false,
  variant = 'default',
  withPagination = true,
  isLink = false,
  getRowLink = undefined,
  collapseComponent: CollapseComponent,
  dataTableTools: DataTableTools,
  onFilterChange,
  manualPagination = false,
  pagination,
  setPagination,
  getRowClassName
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilters || []);
  const [defaultPagination, setDefaultPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPage[0]
  });
  const defaultData = useMemo(() => [], []);
  const paginationState = manualPagination ? {
    pagination
  } : {
    pagination: defaultPagination
  };
  const table = useReactTable({
    data: data ?? defaultData,
    columns,
    pageCount: manualPagination ? pageCount ?? -1 : undefined,
    rowCount,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: manualPagination ? setPagination : setDefaultPagination,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination,
    state: {
      sorting,
      columnFilters,
      ...paginationState
    }
  });
  const router = useRouter();
  const linkFromRow = useCallback((row: TData) => {
    if (isLink && getRowLink) {
      router.push(getRowLink(row));
    }
  }, [getRowLink, isLink, router]);
  return <div className="space-y-2" data-sentry-component="DataTable" data-sentry-source-file="data-table.tsx">
      {DataTableTools && <DataTableTools table={table} onFilterChange={onFilterChange} />}
      {/* TODO: maybe change it back to max height with scrollbar */}
      <div className="mb-4 rounded-md">
        <Table variant={variant} data-sentry-element="Table" data-sentry-source-file="data-table.tsx">
          <TableHeader variant={variant} className="bg-background" data-sentry-element="TableHeader" data-sentry-source-file="data-table.tsx">
            {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id} variant={variant}>
                {headerGroup.headers.map((header, index) => {
              return <TableHead key={header.id} className={mergeClasses(header.column.columnDef.meta?.headerClassName, `${
              // eslint-disable-next-line no-nested-ternary
              index === 0 ? 'rounded-tl-md' : index === headerGroup.headers.length - 1 ? 'rounded-tr-md' : ''}`)}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>;
            })}
              </TableRow>)}
          </TableHeader>

          <TableBody variant={variant} className="border-collapse" data-sentry-element="TableBody" data-sentry-source-file="data-table.tsx">
            {table.getRowModel().rows?.length ? table.getRowModel().rows.map(row => {
            return <Collapsible key={row.id} asChild>
                    <>
                      <TableRow data-state={row.getIsSelected() && 'selected'} variant={variant} className={mergeClasses(isLink ? 'cursor-pointer' : '', getRowClassName?.(row.original))} onClick={() => linkFromRow(row.original)}>
                        {row.getVisibleCells().map(cell => <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>)}
                      </TableRow>
                      {collapsible && CollapseComponent && <CollapsibleContent asChild>
                          <CollapseComponent row={row.original} />
                        </CollapsibleContent>}
                    </>
                  </Collapsible>;
          }) : <TableRow variant={variant}>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {m.data_table_noResults()}
                </TableCell>
              </TableRow>}
          </TableBody>
          {withPagination && <TableFooter variant={variant} className="rounded-b-md bg-background">
              <TableRow variant={variant}>
                <TableCell colSpan={columns.length} className="rounded-b-md p-2">
                  <DataTablePagination table={table} rowsPerPage={rowsPerPage} variant={variant} />
                </TableCell>
              </TableRow>
            </TableFooter>}
        </Table>
      </div>
    </div>;
}